import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Building2, MapPin, Plus, X, ArrowLeft, Mail, Phone, User } from 'lucide-react';
import { getUser } from '@/utils/auth';
import api, { getBackendUrl, getAssetUrl } from '@/utils/api';

const AdminJobPosting = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(false);
  const [primeEmployers, setPrimeEmployers] = useState([]);
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [districtSuggestions, setDistrictSuggestions] = useState([]);
  const [showDistrictSuggestions, setShowDistrictSuggestions] = useState(false);
  const [districtSearch, setDistrictSearch] = useState('');
  const [locations, setLocations] = useState([]);
  const [skills, setSkills] = useState([]);
  const [skillInput, setSkillInput] = useState('');
  
  const [formData, setFormData] = useState({
    prime_employer_id: '',
    job_type: '',
    job_title: '',
    short_info: '',
    description: '',
    gender: [],
    min_experience: 0,
    max_experience: 5,
    min_age: '',
    max_age: '',
    min_salary: '',
    max_salary: '',
    salary_type: 'numeric_range', // 'numeric_range', 'as_per_experience', 'as_per_norms'
    vacancies: 1,
    last_date: '',
    contact_number: '',
    email: '',
    required_education: [],
    course_subject: '',
    publish_on: ['job_portal']  // Default: Job Portal always selected
  });

  const jobTypes = ['Trainee', 'On-Roll', 'Off-Roll', 'Permanent', 'Probation'];
  const educationOptions = ['10th', '12th', 'ITI', 'Diploma', 'UG Degree', 'PG Degree', 'UG Engineering', 'PG Engineering', 'Others'];

  const handleEducationChange = (education, checked) => {
    if (checked) {
      setFormData({ ...formData, required_education: [...formData.required_education, education] });
    } else {
      setFormData({ ...formData, required_education: formData.required_education.filter(e => e !== education) });
    }
  };

  useEffect(() => {
    fetchPrimeEmployers();
  }, []);

  const fetchPrimeEmployers = async () => {
    try {
      const response = await api.get('/admin/prime-employers-list');
      setPrimeEmployers(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch prime employers');
    }
  };

  const handleEmployerSelect = (employerId) => {
    const employer = primeEmployers.find(e => e.id === employerId);
    setSelectedEmployer(employer);
    setFormData({
      ...formData,
      prime_employer_id: employerId,
      contact_number: employer?.mobile_number || '',
      email: employer?.contact_email || ''
    });
  };

  const searchDistricts = async (query) => {
    if (query.length < 2) {
      setDistrictSuggestions([]);
      setShowDistrictSuggestions(false);
      return;
    }
    try {
      // Use unified search API for cities and districts
      const response = await api.get(`/location-master/locations/search?q=${encodeURIComponent(query)}&limit=15`);
      setDistrictSuggestions(response.data);
      setShowDistrictSuggestions(response.data.length > 0);
    } catch (error) {
      console.error('Error searching locations:', error);
    }
  };

  const addLocation = (suggestion) => {
    const newLocation = {
      state: suggestion.state,
      district: suggestion.district,
      city: suggestion.city || suggestion.district
    };
    
    // Check if location already exists (including city)
    const exists = locations.some(
      loc => loc.state === newLocation.state && 
             loc.district === newLocation.district &&
             loc.city === newLocation.city
    );
    
    if (!exists) {
      setLocations([...locations, newLocation]);
    }
    setDistrictSearch('');
    setShowDistrictSuggestions(false);
  };

  const removeLocation = (index) => {
    setLocations(locations.filter((_, i) => i !== index));
  };

  const addSkill = () => {
    if (skillInput.trim() && !skills.includes(skillInput.trim())) {
      setSkills([...skills, skillInput.trim()]);
      setSkillInput('');
    }
  };

  const removeSkill = (index) => {
    setSkills(skills.filter((_, i) => i !== index));
  };

  const handleGenderChange = (gender, checked) => {
    if (checked) {
      setFormData({ ...formData, gender: [...formData.gender, gender] });
    } else {
      setFormData({ ...formData, gender: formData.gender.filter(g => g !== gender) });
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.prime_employer_id) {
      toast.error('Please select a Prime Employer');
      return;
    }
    if (!formData.job_type) {
      toast.error('Please select Job Type');
      return;
    }
    if (!formData.job_title.trim()) {
      toast.error('Job Designation is required');
      return;
    }
    if (!formData.short_info.trim()) {
      toast.error('Short info about job is required');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Roles and Responsibilities is required');
      return;
    }
    if (locations.length === 0) {
      toast.error('At least one location is required');
      return;
    }
    if (formData.gender.length === 0) {
      toast.error('Please select at least one gender');
      return;
    }
    // Min salary is always required
    if (!formData.min_salary) {
      toast.error('Minimum salary is required');
      return;
    }
    if (formData.salary_type === 'numeric_range' && !formData.max_salary) {
      toast.error('Maximum salary is required when selecting "Enter Amount"');
      return;
    }
    if (!formData.last_date) {
      toast.error('Last date to apply is required');
      return;
    }
    if (!formData.contact_number && !formData.email) {
      toast.error('Either Contact Number or Email is required');
      return;
    }
    // For Prime employer jobs, allow multiple comma-separated numbers
    // For regular jobs, validate single 10-digit number
    if (formData.contact_number) {
      // Check if it's a prime employer job (has prime_employer_id)
      if (formData.prime_employer_id) {
        // Allow multiple numbers separated by commas, spaces, or other delimiters
        // Just validate that each number segment has reasonable length
        const numbers = formData.contact_number.split(/[,\s\/\-|]+/).filter(n => n.trim());
        if (numbers.length === 0) {
          toast.error('Please enter valid contact number(s)');
          return;
        }
      } else {
        // Regular job - strict 10 digit validation
        const cleanNumber = formData.contact_number.replace(/\D/g, '');
        if (cleanNumber.length !== 10) {
          toast.error('Contact Number must be exactly 10 digits');
          return;
        }
        if (!/^[6-9]/.test(cleanNumber)) {
          toast.error('Contact Number must start with 6, 7, 8 or 9');
          return;
        }
      }
    }

    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('prime_employer_id', formData.prime_employer_id);
      submitData.append('job_type', formData.job_type);
      submitData.append('job_title', formData.job_title);
      submitData.append('short_info', formData.short_info);
      submitData.append('description', formData.description);
      submitData.append('locations', JSON.stringify(locations));
      submitData.append('skills', JSON.stringify(skills));
      submitData.append('gender', JSON.stringify(formData.gender));
      submitData.append('min_experience', formData.min_experience);
      submitData.append('max_experience', formData.max_experience);
      if (formData.min_age) submitData.append('min_age', formData.min_age);
      if (formData.max_age) submitData.append('max_age', formData.max_age);
      submitData.append('salary_type', formData.salary_type);
      // Always send min_salary, max_salary only for numeric_range
      submitData.append('min_salary', formData.min_salary || '0');
      if (formData.salary_type === 'numeric_range') {
        submitData.append('max_salary', formData.max_salary || '0');
      } else {
        submitData.append('max_salary', '0');
      }
      submitData.append('vacancies', formData.vacancies);
      submitData.append('last_date', formData.last_date);
      if (formData.contact_number) submitData.append('contact_number', formData.contact_number);
      if (formData.email) submitData.append('email', formData.email);
      if (formData.required_education.length > 0) submitData.append('required_education', formData.required_education.join(', '));
      if (formData.course_subject) submitData.append('course_subject', formData.course_subject);
      submitData.append('publish_on', JSON.stringify(formData.publish_on));
      submitData.append('posted_by_id', user.id);
      submitData.append('posted_by_role', user.role);

      await api.post('/admin/prime-employer-jobs', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Job posted successfully! It is now live.');
      navigate('/admin/prime-employer-jobs');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post job');
    } finally {
      setLoading(false);
    }
  };

  const backendUrl = getBackendUrl();

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Post Job for Prime Employer</h1>
          <p className="text-slate-600 text-sm">Jobs posted here will be directly published without approval</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Prime Employer Selection */}
          <Card className="p-6 border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Select Prime Employer</h3>
            <Select value={formData.prime_employer_id} onValueChange={handleEmployerSelect}>
              <SelectTrigger data-testid="prime-employer-select">
                <SelectValue placeholder="Select a Prime Employer" />
              </SelectTrigger>
              <SelectContent>
                {primeEmployers.map((employer) => (
                  <SelectItem key={employer.id} value={employer.id}>
                    <div className="flex items-center gap-2">
                      {employer.logo ? (
                        <img src={getAssetUrl(employer.logo)} className="w-6 h-6 rounded object-contain" alt="" />
                      ) : (
                        <Building2 className="w-4 h-4 text-slate-400" />
                      )}
                      {employer.employer_name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Selected Employer Info */}
            {selectedEmployer && (
              <div className="mt-4 p-4 bg-teal-50 rounded-lg border border-teal-100">
                <div className="flex items-center gap-4">
                  {selectedEmployer.logo ? (
                    <img src={getAssetUrl(selectedEmployer.logo)} className="w-16 h-16 rounded-lg object-contain border bg-white" alt="" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-white border flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-slate-300" />
                    </div>
                  )}
                  <div>
                    <h4 className="font-semibold text-slate-900">{selectedEmployer.employer_name}</h4>
                    <div className="flex flex-wrap gap-3 mt-1 text-sm text-slate-600">
                      {selectedEmployer.contact_person && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {selectedEmployer.contact_person}
                        </span>
                      )}
                      {selectedEmployer.contact_email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {selectedEmployer.contact_email}
                        </span>
                      )}
                      {selectedEmployer.mobile_number && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {selectedEmployer.mobile_number}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Job Details */}
          <Card className="p-6 border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Job Details</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Job Type <span className="text-red-500">*</span></label>
                <Select value={formData.job_type} onValueChange={(v) => setFormData({...formData, job_type: v})}>
                  <SelectTrigger data-testid="job-type-select">
                    <SelectValue placeholder="Select job type" />
                  </SelectTrigger>
                  <SelectContent>
                    {jobTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Job Designation <span className="text-red-500">*</span></label>
                <Input
                  data-testid="job-title-input"
                  value={formData.job_title}
                  onChange={(e) => setFormData({...formData, job_title: e.target.value})}
                  placeholder="e.g. Software Engineer, Sales Executive"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Short info about job <span className="text-red-500">*</span></label>
                <Textarea
                  data-testid="short-info-input"
                  rows={2}
                  value={formData.short_info}
                  onChange={(e) => setFormData({...formData, short_info: e.target.value})}
                  placeholder="Brief description of the job"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Roles and Responsibilities <span className="text-red-500">*</span></label>
                <Textarea
                  data-testid="description-input"
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Detailed job responsibilities"
                />
              </div>
            </div>
          </Card>

          {/* Location */}
          <Card className="p-6 border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Job Locations <span className="text-red-500">*</span></h3>
            <div className="relative mb-4">
              <Input
                data-testid="location-search"
                placeholder="Search city or district (e.g., Hosur, Chennai, Whitefield)..."
                value={districtSearch}
                onChange={(e) => {
                  setDistrictSearch(e.target.value);
                  searchDistricts(e.target.value);
                }}
              />
              {showDistrictSuggestions && districtSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {districtSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      className="w-full px-3 py-2 text-left hover:bg-slate-50 text-sm"
                      onClick={() => addLocation(suggestion)}
                    >
                      <span className="flex items-center gap-2">
                        <MapPin className="w-3 h-3 text-slate-400" />
                        <span>{suggestion.display}</span>
                        {suggestion.type === 'city' && (
                          <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded">City</span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500 mb-4">Supports cities and districts from Tamil Nadu, Karnataka, Kerala, Andhra Pradesh, Telangana, Puducherry</p>
            
            <div className="flex flex-wrap gap-2">
              {locations.map((loc, index) => (
                <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm">
                  <MapPin className="w-3 h-3" />
                  {loc.city && loc.city !== loc.district ? `${loc.city}, ${loc.district}` : loc.district}, {loc.state}
                  <button onClick={() => removeLocation(index)} className="ml-1 hover:text-teal-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </Card>

          {/* Requirements */}
          <Card className="p-6 border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Requirements</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Qualification (Multiple Selection)</label>
                <div className="grid grid-cols-3 gap-3 p-3 border border-slate-200 rounded-lg bg-slate-50">
                  {educationOptions.map((edu) => (
                    <label key={edu} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.required_education.includes(edu)}
                        onCheckedChange={(checked) => handleEducationChange(edu, checked)}
                        data-testid={`edu-${edu.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                      <span className="text-sm">{edu}</span>
                    </label>
                  ))}
                </div>
                {formData.required_education.length > 0 && (
                  <p className="text-xs text-teal-600 mt-2">
                    Selected: {formData.required_education.join(', ')}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Course/Subject <span className="text-slate-400">(Optional)</span></label>
                <Input
                  data-testid="course-subject-input"
                  type="text"
                  placeholder="e.g., COMPUTER SCIENCE, MECHANICAL"
                  value={formData.course_subject || ''}
                  onChange={(e) => setFormData({...formData, course_subject: e.target.value.toUpperCase()})}
                  style={{textTransform: 'uppercase'}}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Experience (Years) <span className="text-red-500">*</span></label>
                <div className="flex gap-2 items-center">
                  <Input
                    data-testid="min-exp-input"
                    type="number"
                    min="0"
                    value={formData.min_experience}
                    onChange={(e) => setFormData({...formData, min_experience: parseInt(e.target.value) || 0})}
                    className="w-20"
                  />
                  <span className="text-slate-500">to</span>
                  <Input
                    data-testid="max-exp-input"
                    type="number"
                    min="0"
                    value={formData.max_experience}
                    onChange={(e) => setFormData({...formData, max_experience: parseInt(e.target.value) || 0})}
                    className="w-20"
                  />
                  <span className="text-slate-500">years</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Gender <span className="text-red-500">*</span></label>
                <div className="flex gap-4">
                  {['Male', 'Female', 'Transgender'].map((gender) => (
                    <label key={gender} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.gender.includes(gender)}
                        onCheckedChange={(checked) => handleGenderChange(gender, checked)}
                      />
                      <span className="text-sm">{gender}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Min Age (Optional)</label>
                <Input
                  data-testid="min-age-input"
                  type="number"
                  min="18"
                  max="65"
                  value={formData.min_age}
                  onChange={(e) => setFormData({...formData, min_age: e.target.value})}
                  placeholder="e.g., 18"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Max Age (Optional)</label>
                <Input
                  data-testid="max-age-input"
                  type="number"
                  min="18"
                  max="65"
                  value={formData.max_age}
                  onChange={(e) => setFormData({...formData, max_age: e.target.value})}
                  placeholder="e.g., 35"
                />
              </div>
            </div>

            {/* Skills */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">Skills (Optional)</label>
              <div className="flex gap-2 mb-2">
                <Input
                  data-testid="skill-input"
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                  placeholder="Add a skill"
                />
                <Button type="button" variant="outline" onClick={addSkill}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {skills.map((skill, index) => (
                  <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                    {skill}
                    <button onClick={() => removeSkill(index)} className="ml-1 hover:text-slate-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </Card>

          {/* Compensation */}
          <Card className="p-6 border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Compensation & Vacancies</h3>
            
            <div className="grid md:grid-cols-3 gap-4">
              {/* Min Salary - Always Required */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Min Salary (₹) <span className="text-red-500">*</span></label>
                <Input
                  data-testid="min-salary-input"
                  type="number"
                  value={formData.min_salary}
                  onChange={(e) => setFormData({...formData, min_salary: e.target.value})}
                  placeholder="e.g., 15000"
                  required
                />
              </div>
              
              {/* Max Salary Type Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Max Salary</label>
                <Select 
                  value={formData.salary_type} 
                  onValueChange={(val) => {
                    if (val === 'numeric_range') {
                      setFormData({...formData, salary_type: 'numeric_range', max_salary: ''});
                    } else {
                      setFormData({...formData, salary_type: val, max_salary: ''});
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select max salary type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="numeric_range">Enter Amount</SelectItem>
                    <SelectItem value="as_per_norms">As Per Norms</SelectItem>
                    <SelectItem value="as_per_experience">As Per Experience</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Max Salary Amount - Only when numeric_range is selected */}
              {formData.salary_type === 'numeric_range' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Max Salary Amount (₹)</label>
                  <Input
                    data-testid="max-salary-input"
                    type="number"
                    value={formData.max_salary}
                    onChange={(e) => setFormData({...formData, max_salary: e.target.value})}
                    placeholder="e.g., 25000"
                  />
                </div>
              )}
              
              {/* Show selected type when not numeric */}
              {formData.salary_type !== 'numeric_range' && formData.salary_type && (
                <div className="flex items-center">
                  <Badge variant="outline" className="px-4 py-2 text-base bg-blue-50 text-blue-700 border-blue-200">
                    Max: {formData.salary_type === 'as_per_experience' ? 'As Per Experience' : 'As Per Norms'}
                  </Badge>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Vacancies <span className="text-red-500">*</span></label>
                <Input
                  data-testid="vacancies-input"
                  type="number"
                  min="1"
                  value={formData.vacancies}
                  onChange={(e) => setFormData({...formData, vacancies: parseInt(e.target.value) || 1})}
                />
              </div>
            </div>
          </Card>

          {/* Contact & Last Date */}
          <Card className="p-6 border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Contact Details</h3>
            <p className="text-sm text-slate-500 mb-4">Either Contact Number or Email is mandatory</p>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Contact Number</label>
                <Input
                  data-testid="contact-number-input"
                  type="tel"
                  value={formData.contact_number}
                  onChange={(e) => {
                    // For prime employer jobs, allow any input (comma separated numbers)
                    if (formData.prime_employer_id) {
                      setFormData({...formData, contact_number: e.target.value});
                    } else {
                      // For regular jobs, only allow digits
                      setFormData({...formData, contact_number: e.target.value.replace(/\D/g, '').slice(0, 10)});
                    }
                  }}
                  placeholder={formData.prime_employer_id ? "Multiple numbers (comma separated)" : "10-digit mobile number"}
                  maxLength={formData.prime_employer_id ? 100 : 10}
                />
                {formData.prime_employer_id && (
                  <p className="text-xs text-slate-500 mt-1">E.g., 9876543210, 8765432109</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <Input
                  data-testid="email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="Enter email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Last Date to Apply <span className="text-red-500">*</span></label>
                <Input
                  data-testid="last-date-input"
                  type="date"
                  value={formData.last_date}
                  onChange={(e) => setFormData({...formData, last_date: e.target.value})}
                />
              </div>
            </div>
          </Card>

          {/* Publishing Options */}
          <Card className="p-6 border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Publishing Options</h3>
            <p className="text-sm text-slate-500 mb-4">I agree to publish this job on:</p>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="admin-publish-job-portal"
                  data-testid="admin-publish-job-portal"
                  checked={true}
                  disabled={true}
                  className="opacity-70"
                />
                <label htmlFor="admin-publish-job-portal" className="text-sm text-slate-700">
                  Job Portal <span className="text-xs text-slate-500">(Default)</span>
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="admin-publish-youtube"
                  data-testid="admin-publish-youtube"
                  checked={formData.publish_on.includes('youtube')}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setFormData({...formData, publish_on: [...formData.publish_on, 'youtube']});
                    } else {
                      setFormData({...formData, publish_on: formData.publish_on.filter(p => p !== 'youtube')});
                    }
                  }}
                />
                <label htmlFor="admin-publish-youtube" className="text-sm text-slate-700">YouTube</label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="admin-publish-whatsapp"
                  data-testid="admin-publish-whatsapp"
                  checked={formData.publish_on.includes('whatsapp')}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setFormData({...formData, publish_on: [...formData.publish_on, 'whatsapp']});
                    } else {
                      setFormData({...formData, publish_on: formData.publish_on.filter(p => p !== 'whatsapp')});
                    }
                  }}
                />
                <label htmlFor="admin-publish-whatsapp" className="text-sm text-slate-700">WhatsApp</label>
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar - Preview */}
        <div className="lg:col-span-1">
          <Card className="p-6 border-slate-200 sticky top-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Job Preview</h3>
            
            {selectedEmployer && (
              <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  {selectedEmployer.logo ? (
                    <img src={getAssetUrl(selectedEmployer.logo)} className="w-10 h-10 rounded object-contain" alt="" />
                  ) : (
                    <Building2 className="w-8 h-8 text-slate-300" />
                  )}
                  <div>
                    <p className="font-medium text-slate-900">{selectedEmployer.employer_name}</p>
                    <p className="text-xs text-slate-500">Posted by {user?.name || 'Admin'}</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-3 text-sm">
              {formData.job_title && (
                <div>
                  <span className="text-slate-500">Designation:</span>
                  <span className="ml-2 font-medium text-slate-900">{formData.job_title}</span>
                </div>
              )}
              {formData.job_type && (
                <div>
                  <span className="text-slate-500">Type:</span>
                  <span className="ml-2 font-medium text-slate-900">{formData.job_type}</span>
                </div>
              )}
              {locations.length > 0 && (
                <div>
                  <span className="text-slate-500">Locations:</span>
                  <span className="ml-2 font-medium text-slate-900">{locations.map(l => l.district).join(', ')}</span>
                </div>
              )}
              {(formData.min_salary || formData.max_salary) && (
                <div>
                  <span className="text-slate-500">Salary:</span>
                  <span className="ml-2 font-medium text-slate-900">₹{formData.min_salary} - ₹{formData.max_salary}</span>
                </div>
              )}
            </div>

            <div className="mt-6 p-3 bg-green-50 rounded-lg border border-green-100">
              <p className="text-sm text-green-700">
                <strong>Note:</strong> This job will be published immediately without approval process.
              </p>
            </div>

            <Button 
              onClick={handleSubmit} 
              className="w-full mt-6 bg-teal-600 hover:bg-teal-700"
              disabled={loading}
              data-testid="submit-job-btn"
            >
              {loading ? 'Posting...' : 'Post Job'}
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminJobPosting;
